import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { businessesAPI } from '@/lib/api';
import GoogleMapDraggable from '@/components/GoogleMapComponents';
import CommissionTermsCard from '@/components/CommissionTermsCard';
import PaymentModeCard from '@/components/PaymentModeCard';
import NotificationPreferencesCard from '@/components/NotificationPreferencesCard';
import { WhatsAppSupportButton } from '@/components/WhatsAppSupport';
import DeleteAccountCard from '@/components/DeleteAccountCard';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, User, ShieldX, MapPin, Search, Loader2,
  Ban, Trash2, Plus, FileText, CreditCard, Building2, Eye, EyeOff,
  Calendar, Shield, ExternalLink, Save, Pencil, Clock, Bell, MessageSquare,
  Upload, AlertTriangle, CheckCircle2, XCircle
} from 'lucide-react';

export default function BusinessSettingsPage() {
  const { language } = useI18n();
  const { isAuthenticated, isBusiness, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const t = (es, en) => language === 'es' ? es : en;

  const [loading, setLoading] = useState(true);
  // Read initial tab from `?tab=...` so deep links from the dashboard land on
  // the right section (e.g. ?tab=hours from the profile-completion banner).
  const getInitialTab = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      const allowed = ['info', 'hours', 'notifications', 'documents', 'commission', 'subscription', 'location', 'blacklist'];
      return allowed.includes(t) ? t : 'info';
    } catch { return 'info'; }
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Live Stripe Connect status. On mount we hit /stripe-connect/status which
  // fetches the real state from Stripe and re-syncs it into MongoDB. This
  // guards against stale `stripe_connect_charges_enabled=false` values in the
  // business doc when the account.updated webhook never fired.
  const [stripeStatus, setStripeStatus] = useState(null);

  // Business info
  const [privateInfo, setPrivateInfo] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Sensitive data visibility
  const [showClabe, setShowClabe] = useState(false);
  const [showRfc, setShowRfc] = useState(false);

  // Blacklist
  const [blacklist, setBlacklist] = useState([]);
  const [adding, setAdding] = useState(false);
  const [blForm, setBlForm] = useState({ email: '', phone: '', user_id: '', reason: '' });

  // Location
  const [locationSearch, setLocationSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);

  // Business hours
  const DAY_NAMES = language === 'es'
    ? ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [businessHours, setBusinessHours] = useState(null);
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isBusiness) { navigate('/business/login'); return; }
    loadData();
  }, [isAuthenticated, isBusiness, authLoading]);

  const loadData = async () => {
    try {
      const [infoRes, blRes, dashRes, hoursRes, stripeRes] = await Promise.all([
        businessesAPI.getPrivateInfo().catch(() => null),
        businessesAPI.getBlacklist().catch(() => ({ data: [] })),
        businessesAPI.getDashboard().catch(() => null),
        businessesAPI.getBusinessHours().catch(() => null),
        businessesAPI.connectStatus().catch(() => null),
      ]);
      if (infoRes?.data) {
        setPrivateInfo(infoRes.data);
        setEditForm({
          name: infoRes.data.name || '',
          phone: infoRes.data.phone || '',
          description: infoRes.data.description || '',
        });
      }
      setBlacklist(Array.isArray(blRes?.data) ? blRes.data : []);
      const biz = dashRes?.data?.business;
      if (biz?.latitude && biz?.longitude) {
        setCurrentLocation({ lat: biz.latitude, lng: biz.longitude, address: biz.address, city: biz.city, state: biz.state });
      }
      if (hoursRes?.data) setBusinessHours(hoursRes.data);
      if (stripeRes?.data) setStripeStatus(stripeRes.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await businessesAPI.updateBusiness({
        name: editForm.name,
        description: editForm.description,
      });
      setPrivateInfo(prev => ({ ...prev, name: editForm.name, description: editForm.description }));
      setEditMode(false);
      toast.success(t('Informacion guardada', 'Information saved'));
    } catch {
      toast.error(t('Error al guardar', 'Error saving'));
    }
    setSaving(false);
  };

  const [savingPrefs, setSavingPrefs] = useState(false);
  const toggleNotifPref = async (field, nextValue) => {
    setSavingPrefs(true);
    try {
      await businessesAPI.updateBusiness({ [field]: nextValue });
      setPrivateInfo(prev => ({ ...prev, [field]: nextValue }));
      toast.success(t('Preferencias actualizadas', 'Preferences updated'));
    } catch {
      toast.error(t('Error al guardar', 'Error saving'));
    }
    setSavingPrefs(false);
  };

  // --- Fase 8: Legal documents editing ---
  const [docsEditMode, setDocsEditMode] = useState(false);
  const [docsForm, setDocsForm] = useState({});
  const [savingDocs, setSavingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'ine' | 'proof' | 'bank' | null

  const startDocsEdit = () => {
    setDocsForm({
      legal_name: privateInfo?.legal_name || '',
      rfc: privateInfo?.rfc || '',
      clabe: privateInfo?.clabe || '',
      ine_url: privateInfo?.ine_url || '',
      proof_of_address_url: privateInfo?.proof_of_address_url || '',
      bank_proof_url: privateInfo?.bank_proof_url || '',
    });
    setDocsEditMode(true);
  };

  // Legacy documents were uploaded to Emergent Object Storage (via /api/files/…)
  // which is now unreachable, so those URLs 404 when clicked. Detect them so we
  // can hide broken preview links and prompt the user to re-upload.
  const isLegacyBrokenDoc = (url) => typeof url === 'string' && url.includes('/api/files/bookvia/businesses/registration/');
  const hasAnyLegacyBrokenDoc = privateInfo && (
    isLegacyBrokenDoc(privateInfo.ine_url) ||
    isLegacyBrokenDoc(privateInfo.proof_of_address_url) ||
    isLegacyBrokenDoc(privateInfo.bank_proof_url)
  );

  // Sensitive legal documents are stored on Cloudinary as `authenticated`.
  // We request a short-lived signed URL from the backend before opening.
  const [openingDoc, setOpeningDoc] = useState(null);
  const openLegalDocument = async (docType) => {
    if (!privateInfo?.id) return;
    if (openingDoc) return;
    setOpeningDoc(docType);
    try {
      const res = await businessesAPI.getSignedDocumentUrl(privateInfo.id, docType);
      const url = res.data?.url;
      if (!url) throw new Error('No URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Error';
      toast.error(t('No se pudo abrir el documento', 'Could not open document') + ': ' + msg);
    } finally {
      setOpeningDoc(null);
    }
  };

  const uploadLegalDoc = async (field, file) => {
    if (!file) return;
    setUploadingDoc(field);
    try {
      const res = await businessesAPI.uploadPublicFile(file);
      const url = res.data?.url;
      setDocsForm(prev => ({ ...prev, [field]: url }));
      toast.success(t('Archivo subido', 'File uploaded'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Error al subir archivo', 'Error uploading'));
    } finally {
      setUploadingDoc(null);
    }
  };

  const saveLegalDocs = async () => {
    setSavingDocs(true);
    try {
      const payload = {};
      const fields = ['legal_name', 'rfc', 'clabe', 'ine_url', 'proof_of_address_url', 'bank_proof_url'];
      for (const f of fields) {
        if ((docsForm[f] || '') !== (privateInfo?.[f] || '')) payload[f] = docsForm[f] || '';
      }
      if (Object.keys(payload).length === 0) {
        toast.info(t('Sin cambios', 'No changes'));
        setDocsEditMode(false);
        setSavingDocs(false);
        return;
      }
      const res = await businessesAPI.updateLegalDocs(payload);
      setPrivateInfo(prev => ({
        ...prev,
        ...payload,
        documents_verified: res.data?.documents_verified ?? false,
        documents_rejection_reason: null,
      }));
      setDocsEditMode(false);
      if (res.data?.requires_review) {
        toast.success(t('Documentos enviados a revision. Bookvia te notificara cuando sean verificados.', 'Documents sent for review. You will be notified when they are verified.'));
      } else {
        toast.success(t('Documentos actualizados', 'Documents updated'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Error al guardar', 'Error saving'));
    }
    setSavingDocs(false);
  };

  // Location handlers
  const searchAddress = async () => {
    if (!locationSearch.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationSearch)}&limit=5&countrycodes=mx&addressdetails=1`, {
        headers: { 'Accept-Language': language === 'es' ? 'es' : 'en' }
      });
      const data = await res.json();
      setSearchResults(data.map(r => ({
        display: r.display_name, lat: parseFloat(r.lat), lng: parseFloat(r.lon),
        city: r.address?.city || r.address?.town || r.address?.municipality || '',
        state: r.address?.state || '',
      })));
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  const selectLocation = async (loc) => {
    setSavingLocation(true);
    try {
      await businessesAPI.updateBusiness({
        latitude: loc.lat, longitude: loc.lng,
        address: loc.display.split(',')[0], city: loc.city, state: loc.state,
      });
      setCurrentLocation({ lat: loc.lat, lng: loc.lng, address: loc.display.split(',')[0], city: loc.city, state: loc.state });
      setSearchResults([]);
      setLocationSearch('');
      toast.success(t('Ubicacion guardada', 'Location saved'));
    } catch { toast.error(t('Error al guardar', 'Save error')); }
    setSavingLocation(false);
  };

  // Blacklist handlers
  const handleAddBlacklist = async () => {
    if (!blForm.email && !blForm.phone && !blForm.user_id) {
      toast.error(t('Ingresa al menos un identificador', 'Enter at least one identifier'));
      return;
    }
    setAdding(true);
    try {
      const payload = {};
      if (blForm.email.trim()) payload.email = blForm.email.trim();
      if (blForm.phone.trim()) payload.phone = blForm.phone.trim();
      if (blForm.user_id.trim()) payload.user_id = blForm.user_id.trim();
      if (blForm.reason.trim()) payload.reason = blForm.reason.trim();
      await businessesAPI.addToBlacklist(payload);
      toast.success(t('Cliente vetado correctamente', 'Client banned successfully'));
      setBlForm({ email: '', phone: '', user_id: '', reason: '' });
      const res = await businessesAPI.getBlacklist();
      setBlacklist(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error al vetar', 'Error banning'));
    }
    setAdding(false);
  };

  const handleRemoveBlacklist = async (entryId) => {
    if (!window.confirm(t('Quitar este veto?', 'Remove this ban?'))) return;
    try {
      await businessesAPI.removeFromBlacklist(entryId);
      setBlacklist(prev => prev.filter(e => e.id !== entryId));
      toast.success(t('Veto eliminado', 'Ban removed'));
    } catch { toast.error('Error'); }
  };

  const maskValue = (val, showChars = 4) => {
    if (!val) return '---';
    if (val.length <= showChars) return val;
    return '*'.repeat(val.length - showChars) + val.slice(-showChars);
  };

  const tabs = [
    { id: 'info', label: t('Informacion', 'Information'), icon: Building2 },
    { id: 'hours', label: t('Horarios', 'Hours'), icon: Clock },
    { id: 'notifications', label: t('Notificaciones', 'Notifications'), icon: Bell },
    { id: 'documents', label: t('Documentos', 'Documents'), icon: FileText },
    { id: 'commission', label: t('Cobros', 'Payments'), icon: CreditCard },
    { id: 'subscription', label: t('Suscripcion', 'Subscription'), icon: CreditCard },
    { id: 'location', label: t('Ubicacion', 'Location'), icon: MapPin },
    { id: 'blacklist', label: t('Vetos', 'Bans'), icon: ShieldX },
  ];

  // Check if user is manager (not owner)
  const isManager = user?.worker_id;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-background">
        <div className="container-app py-8 max-w-3xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-12 mb-4" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-background" data-testid="business-settings-page">
      <div className="container-app py-8 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/business/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-heading font-bold">{t('Configuracion', 'Settings')}</h1>
        </div>

        {/* Deposit misconfiguration banner —
            The business toggled "Requires deposit" but Stripe Connect isn't
            fully enabled (charges disabled or account never connected).
            Trust `stripeStatus` (live from Stripe via /stripe-connect/status)
            when available; fall back to the cached DB flag if that call
            failed. This avoids false positives when the account.updated
            webhook was missed but the account is actually fine. */}
        {(() => {
          if (!privateInfo || !privateInfo.requires_deposit) return null;
          const stripeReady = stripeStatus
            ? (stripeStatus.charges_enabled && stripeStatus.payouts_enabled && stripeStatus.details_submitted)
            : privateInfo.stripe_connect_charges_enabled;
          if (stripeReady) return null;
          return (
          <div
            className="mb-6 rounded-2xl border-2 border-red-400 bg-red-50 dark:bg-red-950/40 dark:border-red-800 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-3"
            data-testid="deposit-misconfig-banner"
          >
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="shrink-0 h-9 w-9 rounded-xl bg-red-500 text-white flex items-center justify-center">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-red-900 dark:text-red-100 text-sm sm:text-base leading-snug">
                  {t(
                    'Tienes "Requiere Anticipo" activado, pero tu Stripe Connect no está completo.',
                    'You have "Requires Deposit" enabled, but your Stripe Connect setup is incomplete.'
                  )}
                </p>
                <p className="text-xs sm:text-sm text-red-800 dark:text-red-200 mt-1 leading-relaxed">
                  {t(
                    'Los clientes no pueden reservar contigo hasta que completes tu configuración de pagos. Estás perdiendo reservas en silencio.',
                    'Clients cannot book with you until you finish your payment setup. You are silently losing bookings.'
                  )}
                </p>
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  const res = await businessesAPI.connectOnboard();
                  if (res.data?.url) {
                    window.location.href = res.data.url;
                  } else {
                    toast.error(t('No se pudo generar el link de Stripe', 'Could not generate Stripe link'));
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.detail || t('Error al iniciar onboarding', 'Error starting onboarding'));
                }
              }}
              className="shrink-0 bg-red-600 hover:bg-red-700 text-white rounded-full h-10 px-5 gap-2 w-full sm:w-auto"
              data-testid="deposit-misconfig-complete-btn"
            >
              {t('Completar ahora', 'Complete now')}
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
          );
        })()}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1" data-testid="settings-tabs">
          {tabs.filter(tab => !(isManager && (tab.id === 'documents' || tab.id === 'subscription' || tab.id === 'commission'))).map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              className={activeTab === tab.id ? 'btn-coral shrink-0' : 'shrink-0'}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 mr-1.5" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* ===================== INFO TAB ===================== */}
        {activeTab === 'info' && privateInfo && (
          <div className="space-y-4">
            {/* Help / WhatsApp support card */}
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-transparent" data-testid="settings-help-card">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900">
                    {t('¿Necesitas ayuda?', 'Need help?')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {t(
                      'Resolvemos tus dudas por WhatsApp en horario laboral (9:00–19:00 hrs CDMX, lun–vie).',
                      'We answer your questions on WhatsApp during business hours (9:00–19:00 Mexico City time, Mon–Fri).',
                    )}
                  </p>
                </div>
                <WhatsAppSupportButton
                  variant="default"
                  size="default"
                  businessName={privateInfo.name}
                  publicCode={privateInfo.public_code}
                  className="bg-[#25D366] hover:bg-[#22c55e] text-white border-0"
                  dataTestId="settings-whatsapp-btn"
                />
              </CardContent>
            </Card>

            {/* Public Code Card - prominent */}
            {privateInfo.public_code && (
              <Card className="border-[#F05D5E]/30 bg-gradient-to-br from-[#F05D5E]/5 to-transparent" data-testid="public-code-card">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#F05D5E]">
                        {t('Tu codigo Bookvia', 'Your Bookvia code')}
                      </p>
                      <p className="font-mono font-bold text-2xl sm:text-3xl text-slate-900 mt-1 tracking-wider" data-testid="public-code-value">
                        {privateInfo.public_code}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1.5 max-w-md leading-relaxed">
                        {t(
                          'Compartelo en tarjetas, redes o cuando llames a soporte. Sirve como identificacion unica de tu negocio.',
                          'Share it on cards, social media, or when calling support. It is your business unique identifier.'
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(privateInfo.public_code);
                        toast.success(t('Codigo copiado', 'Code copied'));
                      }}
                      className="shrink-0 border-[#F05D5E] text-[#F05D5E] hover:bg-[#F05D5E] hover:text-white"
                      data-testid="copy-public-code-btn"
                    >
                      <Save className="h-4 w-4 mr-1.5" />
                      {t('Copiar', 'Copy')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ─── Cancellation Policy Card ─── Phase O */}
            <Card data-testid="settings-cancellation-card">
              <CardHeader>
                <CardTitle className="text-base font-heading">
                  {t('Politica de cancelacion', 'Cancellation policy')}
                </CardTitle>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(
                    'Define cuantas horas antes de la cita un cliente puede cancelar y recibir reembolso del anticipo. Entre 1 y 72 horas (3 dias maximo, por proteccion al consumidor).',
                    'Set how many hours before the appointment a client can cancel and get the deposit refund. Between 1 and 72 hours (3-day cap, for consumer protection).'
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label htmlFor="cancellation-hours-select" className="text-sm font-medium shrink-0">
                    {t('Hasta cuantas horas antes:', 'Up to how many hours before:')}
                  </label>
                  <select
                    id="cancellation-hours-select"
                    data-testid="settings-cancellation-hours-select"
                    value={privateInfo.cancellation_hours || (privateInfo.cancellation_days ? privateInfo.cancellation_days * 24 : 24)}
                    onChange={async (e) => {
                      const hours = Math.min(72, Math.max(1, Number(e.target.value) || 24));
                      try {
                        await businessesAPI.updateBusiness({
                          cancellation_hours: hours,
                          cancellation_days: Math.max(1, Math.ceil(hours / 24)),
                        });
                        setPrivateInfo(prev => ({
                          ...prev,
                          cancellation_hours: hours,
                          cancellation_days: Math.max(1, Math.ceil(hours / 24)),
                        }));
                        toast.success(t('Politica actualizada', 'Policy updated'));
                      } catch {
                        toast.error(t('Error al guardar', 'Error saving'));
                      }
                    }}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm min-w-[140px]"
                  >
                    {[1, 2, 4, 6, 12, 24, 48, 72].map((h) => (
                      <option key={h} value={h}>
                        {h} {h === 1 ? t('hora', 'hour') : t('horas', 'hours')}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3 leading-relaxed">
                  {t(
                    'Si el cliente cancela despues de este margen, el anticipo se queda contigo y se reporta como cancelacion tardia. Antes del margen, se le devuelve el anticipo.',
                    'If a client cancels past this window, the deposit stays with you and is logged as late cancellation. Before the window, the deposit is refunded.'
                  )}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="info-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#F05D5E]" />
                {t('Informacion del negocio', 'Business information')}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditMode(!editMode)} data-testid="edit-info-btn">
                <Pencil className="h-4 w-4 mr-1" />{editMode ? t('Cancelar', 'Cancel') : t('Editar', 'Edit')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editMode ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('Nombre del negocio', 'Business name')}</Label>
                    <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Descripcion', 'Description')}</Label>
                    <Textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={3} data-testid="edit-description" />
                  </div>
                  <Button className="btn-coral" onClick={handleSaveInfo} disabled={saving} data-testid="save-info-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {t('Guardar cambios', 'Save changes')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <InfoRow icon={Building2} label={t('Nombre', 'Name')} value={privateInfo.name} />
                  <InfoRow icon={Mail} label="Email" value={privateInfo.email} />
                  <InfoRow icon={Phone} label={t('Telefono', 'Phone')} value={privateInfo.phone} />
                  <Separator />
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Descripcion', 'Description')}</Label>
                    <p className="text-sm mt-1 leading-relaxed">{privateInfo.description || t('Sin descripcion', 'No description')}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {/* ===================== HOURS TAB ===================== */}
        {activeTab === 'hours' && businessHours && (
          <Card data-testid="hours-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#F05D5E]" />
                  {t('Horarios de apertura', 'Opening hours')}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Define los horarios en que tu negocio atiende cada dia.', 'Set when your business is open each day.')}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {DAY_NAMES.map((dayName, i) => {
                const dayKey = String(i);
                const day = businessHours[dayKey] || { is_open: false, open_time: '09:00', close_time: '18:00' };
                return (
                  <div key={i} className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl border transition-colors ${day.is_open ? 'bg-green-50/50 border-green-200 dark:bg-green-900/10 dark:border-green-800' : 'bg-muted/30'}`}
                    data-testid={`day-row-${i}`}>
                    <div className="flex items-center justify-between sm:justify-start gap-3 min-w-0">
                      <div className="w-20 sm:w-24 shrink-0">
                        <p className={`text-sm font-medium ${day.is_open ? 'text-foreground' : 'text-muted-foreground'}`}>{dayName}</p>
                      </div>

                      <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={day.is_open}
                          onChange={e => {
                            setBusinessHours(prev => ({
                              ...prev,
                              [dayKey]: { ...prev[dayKey], is_open: e.target.checked }
                            }));
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-[#F05D5E] focus:ring-[#F05D5E]"
                          data-testid={`day-toggle-${i}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {day.is_open ? t('Abierto', 'Open') : t('Cerrado', 'Closed')}
                        </span>
                      </label>
                    </div>

                    {day.is_open && (
                      <div className="flex items-center gap-2 sm:ml-auto">
                        <Input
                          type="time"
                          value={day.open_time || '09:00'}
                          onChange={e => setBusinessHours(prev => ({
                            ...prev,
                            [dayKey]: { ...prev[dayKey], open_time: e.target.value }
                          }))}
                          className="flex-1 min-w-0 sm:flex-none sm:w-28 h-9 text-sm"
                          data-testid={`day-open-${i}`}
                        />
                        <span className="text-muted-foreground text-sm shrink-0">—</span>
                        <Input
                          type="time"
                          value={day.close_time || '18:00'}
                          onChange={e => setBusinessHours(prev => ({
                            ...prev,
                            [dayKey]: { ...prev[dayKey], close_time: e.target.value }
                          }))}
                          className="flex-1 min-w-0 sm:flex-none sm:w-28 h-9 text-sm"
                          data-testid={`day-close-${i}`}
                        />
                      </div>
                    )}

                    {!day.is_open && (
                      <p className="sm:ml-auto text-sm text-muted-foreground italic">{t('Cerrado', 'Closed')}</p>
                    )}
                  </div>
                );
              })}

              <Button
                className="w-full btn-coral mt-4"
                disabled={savingHours}
                onClick={async () => {
                  setSavingHours(true);
                  try {
                    await businessesAPI.updateBusinessHours(businessHours);
                    toast.success(t('Horarios guardados', 'Hours saved'));
                  } catch {
                    toast.error(t('Error al guardar horarios', 'Error saving hours'));
                  }
                  setSavingHours(false);
                }}
                data-testid="save-hours-btn"
              >
                {savingHours ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t('Guardar horarios', 'Save hours')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ===================== DOCUMENTS TAB ===================== */}
        {activeTab === 'documents' && privateInfo && !isManager && (
          <Card data-testid="documents-section">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#F05D5E]" />
                    {t('Documentos legales', 'Legal documents')}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Solo tu como dueno puedes ver esta informacion.', 'Only you as the owner can see this information.')}
                  </p>
                </div>
                {!docsEditMode && (
                  <Button size="sm" variant="outline" onClick={startDocsEdit} data-testid="edit-docs-btn">
                    <Pencil className="h-4 w-4 mr-1" /> {t('Editar', 'Edit')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* VERIFICATION STATUS BANNER */}
              {privateInfo.documents_verified ? (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800" data-testid="docs-verified-banner">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-green-800 dark:text-green-300">{t('Documentos verificados', 'Documents verified')}</p>
                    <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                      {t('Tu negocio puede recibir reservas y pagos. Si cambias tu CLABE u otros documentos, pasaras a revision de nuevo.',
                         'Your business can receive bookings and payments. If you change your CLABE or other documents you will need re-verification.')}
                    </p>
                  </div>
                </div>
              ) : privateInfo.documents_rejection_reason ? (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800" data-testid="docs-rejected-banner">
                  <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800 dark:text-red-300">{t('Documentos rechazados', 'Documents rejected')}</p>
                    <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                      {t('Motivo:', 'Reason:')} <span className="italic">{privateInfo.documents_rejection_reason}</span>
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                      {t('No podras recibir reservas hasta que corrijas y vuelvas a enviar.', 'You cannot accept bookings until you correct and resubmit.')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800" data-testid="docs-pending-banner">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-300">{t('Documentos en revision', 'Documents under review')}</p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                      {t('Bookvia esta revisando tus documentos. Tu negocio no recibira reservas hasta que sean aprobados.',
                         'Bookvia is reviewing your documents. Your business will not receive bookings until they are approved.')}
                    </p>
                  </div>
                </div>
              )}

              {!docsEditMode ? (
                <>
                  <InfoRow icon={User} label={t('Razon Social', 'Legal Name')} value={privateInfo.legal_name} />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">RFC</p>
                        <p className="text-sm font-medium font-mono" data-testid="rfc-value">
                          {showRfc ? privateInfo.rfc : maskValue(privateInfo.rfc)}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowRfc(!showRfc)} data-testid="toggle-rfc">
                      {showRfc ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">CLABE</p>
                        <p className="text-sm font-medium font-mono" data-testid="clabe-value">
                          {showClabe ? privateInfo.clabe : maskValue(privateInfo.clabe)}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowClabe(!showClabe)} data-testid="toggle-clabe">
                      {showClabe ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>

                  <InfoRow icon={Calendar} label={t('Fecha de nacimiento', 'Birth date')} value={privateInfo.owner_birth_date || '---'} />

                  <Separator />

                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('Archivos subidos', 'Uploaded files')}</p>

                    {privateInfo.ine_url ? (
                      isLegacyBrokenDoc(privateInfo.ine_url) ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800" data-testid="ine-legacy">
                          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-red-800 dark:text-red-300">{t('Identificacion oficial (INE)', 'Official ID (INE)')}</p>
                            <p className="text-xs text-red-700 dark:text-red-400">{t('Archivo ya no disponible. Sube uno nuevo.', 'File no longer available. Upload a new one.')}</p>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => openLegalDocument('ine')} disabled={openingDoc === 'ine'}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left disabled:opacity-60" data-testid="ine-link">
                          <FileText className="h-5 w-5 text-blue-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{t('Identificacion oficial (INE)', 'Official ID (INE)')}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {openingDoc === 'ine' ? t('Abriendo...', 'Opening...') : t('Clic para ver (link temporal seguro)', 'Click to view (secure temporary link)')}
                            </p>
                          </div>
                          <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      )
                    ) : (
                      <p className="text-sm text-muted-foreground italic">{t('INE no subida', 'INE not uploaded')}</p>
                    )}

                    {privateInfo.proof_of_address_url ? (
                      isLegacyBrokenDoc(privateInfo.proof_of_address_url) ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800" data-testid="proof-legacy">
                          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-red-800 dark:text-red-300">{t('Constancia de Situacion Fiscal', 'Tax Status Certificate')}</p>
                            <p className="text-xs text-red-700 dark:text-red-400">{t('Archivo ya no disponible. Sube uno nuevo.', 'File no longer available. Upload a new one.')}</p>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => openLegalDocument('constancia')} disabled={openingDoc === 'constancia'}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left disabled:opacity-60" data-testid="proof-link">
                          <FileText className="h-5 w-5 text-green-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{t('Constancia de Situacion Fiscal', 'Tax Status Certificate')}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {openingDoc === 'constancia' ? t('Abriendo...', 'Opening...') : t('Clic para ver (link temporal seguro)', 'Click to view (secure temporary link)')}
                            </p>
                          </div>
                          <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      )
                    ) : (
                      <p className="text-sm text-muted-foreground italic">{t('Constancia no subida', 'Certificate not uploaded')}</p>
                    )}

                    {privateInfo.bank_proof_url ? (
                      isLegacyBrokenDoc(privateInfo.bank_proof_url) ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800" data-testid="bank-proof-legacy">
                          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-red-800 dark:text-red-300">{t('Comprobante bancario (CLABE)', 'Bank proof (CLABE)')}</p>
                            <p className="text-xs text-red-700 dark:text-red-400">{t('Archivo ya no disponible. Sube uno nuevo.', 'File no longer available. Upload a new one.')}</p>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => openLegalDocument('bank_proof')} disabled={openingDoc === 'bank_proof'}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left disabled:opacity-60" data-testid="bank-proof-link">
                          <FileText className="h-5 w-5 text-indigo-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{t('Comprobante bancario (CLABE)', 'Bank proof (CLABE)')}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {openingDoc === 'bank_proof' ? t('Abriendo...', 'Opening...') : t('Clic para ver (link temporal seguro)', 'Click to view (secure temporary link)')}
                            </p>
                          </div>
                          <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      )
                    ) : (
                      <p className="text-sm text-red-600 italic" data-testid="bank-proof-missing">
                        {t('Comprobante bancario NO subido (obligatorio para verificar tu CLABE)', 'Bank proof NOT uploaded (required to verify your CLABE)')}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                /* ======= EDIT MODE ======= */
                <div className="space-y-4" data-testid="docs-edit-form">
                  {hasAnyLegacyBrokenDoc && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800" data-testid="legacy-docs-warning">
                      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800 dark:text-red-300">
                        {t('Tus documentos actuales se subieron antes de nuestra migracion y ya no estan disponibles. Por favor vuelve a subir los archivos marcados en rojo.',
                           'Your current documents were uploaded before our migration and are no longer available. Please re-upload the files marked in red.')}
                      </p>
                    </div>
                  )}

                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                    <AlertTriangle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 dark:text-blue-300">
                      {t('Al modificar cualquier documento o la CLABE, Bookvia revisara tu cuenta antes de que puedas recibir nuevas reservas.',
                         'When you change any document or the CLABE, Bookvia will re-review your account before you can accept new bookings.')}
                    </p>
                  </div>

                  <div>
                    <Label>{t('Razon Social', 'Legal Name')}</Label>
                    <Input data-testid="docs-legal-name-input" value={docsForm.legal_name || ''} onChange={(e) => setDocsForm(p => ({ ...p, legal_name: e.target.value }))} />
                  </div>

                  <div>
                    <Label>RFC</Label>
                    <Input data-testid="docs-rfc-input" value={docsForm.rfc || ''} onChange={(e) => setDocsForm(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} maxLength={13} />
                  </div>

                  <div>
                    <Label>CLABE</Label>
                    <Input data-testid="docs-clabe-input" value={docsForm.clabe || ''} onChange={(e) => setDocsForm(p => ({ ...p, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) }))} maxLength={18} />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('18 digitos. Cambiar la CLABE requiere subir nuevo comprobante bancario y volver a verificacion.',
                         '18 digits. Changing CLABE requires uploading a new bank proof and re-verification.')}
                    </p>
                  </div>

                  {/* File uploads */}
                  {[
                    { field: 'ine_url', label: t('Identificacion oficial (INE)', 'Official ID (INE)'), testid: 'upload-ine' },
                    { field: 'proof_of_address_url', label: t('Constancia Situacion Fiscal', 'Tax Status Certificate'), testid: 'upload-proof' },
                    { field: 'bank_proof_url', label: t('Comprobante bancario (debe mostrar la CLABE)', 'Bank proof (must show the CLABE)'), testid: 'upload-bank', required: true },
                  ].map(({ field, label, testid, required }) => (
                    <div key={field}>
                      <Label>{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="file" id={`file-${field}`} accept="image/*,application/pdf"
                          onChange={(e) => uploadLegalDoc(field, e.target.files?.[0])}
                          className="hidden" data-testid={`${testid}-input`} />
                        <label htmlFor={`file-${field}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-muted/50 transition-colors">
                            {uploadingDoc === field ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : (
                              <Upload className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-sm truncate">
                              {docsForm[field] ? t('Archivo listo (clic para reemplazar)', 'File ready (click to replace)') : t('Subir archivo (JPG, PNG o PDF)', 'Upload file (JPG, PNG or PDF)')}
                            </span>
                          </div>
                        </label>
                        {docsForm[field] && !isLegacyBrokenDoc(docsForm[field]) && (
                          <a href={docsForm[field]} target="_blank" rel="noopener noreferrer">
                            <Button size="icon" variant="ghost" className="h-9 w-9"><ExternalLink className="h-4 w-4" /></Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-2 pt-2">
                    <Button className="btn-coral" onClick={saveLegalDocs} disabled={savingDocs} data-testid="save-docs-btn">
                      {savingDocs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {t('Guardar y enviar a revision', 'Save and submit for review')}
                    </Button>
                    <Button variant="outline" onClick={() => setDocsEditMode(false)} disabled={savingDocs} data-testid="cancel-docs-btn">
                      {t('Cancelar', 'Cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===================== COMMISSION TERMS TAB (Phase 19) ===================== */}
        {activeTab === 'commission' && privateInfo && !isManager && (
          <div className="space-y-4">
            <PaymentModeCard language={language} onChanged={loadData} />
            <CommissionTermsCard
              privateInfo={privateInfo}
              language={language}
              onRefresh={loadData}
            />
          </div>
        )}

        {/* ===================== SUBSCRIPTION TAB ===================== */}
        {activeTab === 'subscription' && privateInfo && !isManager && (
          <Card data-testid="subscription-section">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#F05D5E]" />
                {t('Suscripcion y facturacion', 'Subscription & billing')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border">
                <div>
                  <p className="text-xs text-muted-foreground">{t('Estado', 'Status')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={
                      privateInfo.subscription_status === 'active' || privateInfo.subscription_status === 'trialing' 
                        ? 'default' : 'secondary'
                    } className={
                      privateInfo.subscription_status === 'active' || privateInfo.subscription_status === 'trialing'
                        ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''
                    } data-testid="subscription-status">
                      {privateInfo.subscription_status === 'trialing' ? t('Periodo de prueba', 'Trial period') :
                       privateInfo.subscription_status === 'active' ? t('Activa', 'Active') :
                       privateInfo.subscription_status === 'none' ? t('Sin suscripcion', 'No subscription') :
                       privateInfo.subscription_status}
                    </Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{t('Plan', 'Plan')}</p>
                  <p className="text-sm font-semibold mt-1">{privateInfo.country_code === 'US' ? `$4.99 USD / ${t('mes', 'month')}` : `$49.99 MXN / ${t('mes', 'month')}`}</p>
                </div>
              </div>

              {/* Subscription details */}
              {privateInfo.subscription_info && (
                <div className="space-y-3">
                  {privateInfo.subscription_info.trial_end && (
                    <InfoRow icon={Calendar} label={t('Prueba termina', 'Trial ends')} 
                      value={new Date(privateInfo.subscription_info.trial_end).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })} />
                  )}
                  
                  {privateInfo.subscription_info.current_period_end && (
                    <InfoRow icon={Calendar} label={t('Proximo cobro', 'Next billing')} 
                      value={new Date(privateInfo.subscription_info.current_period_end).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })} />
                  )}

                  {privateInfo.subscription_info.cancel_at_period_end && (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 p-3">
                      <p className="text-sm text-amber-700 font-medium">{t('Tu suscripcion se cancelara al final del periodo actual', 'Your subscription will cancel at the end of the current period')}</p>
                    </div>
                  )}

                  {/* Card info */}
                  {privateInfo.subscription_info.card_last4 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20" data-testid="card-info">
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium capitalize">
                          {privateInfo.subscription_info.card_brand} **** {privateInfo.subscription_info.card_last4}
                        </p>
                        <p className="text-xs text-muted-foreground">{t('Metodo de pago registrado', 'Registered payment method')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {privateInfo.subscription_started_at && (
                <InfoRow icon={Calendar} label={t('Suscrito desde', 'Subscribed since')} 
                  value={new Date(privateInfo.subscription_started_at).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })} />
              )}
            </CardContent>
          </Card>
        )}

        {/* ===================== LOCATION TAB ===================== */}
        {activeTab === 'location' && (
          <Card data-testid="location-section">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#F05D5E]" />
                {t('Ubicacion del negocio', 'Business location')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t('Busca tu direccion para mostrar el mapa en tu perfil publico.', 'Search your address to show the map on your public profile.')}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder={t('Buscar direccion...', 'Search address...')}
                  value={locationSearch}
                  onChange={e => setLocationSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchAddress()}
                  data-testid="location-search-input"
                />
                <Button variant="outline" onClick={searchAddress} disabled={searching || !locationSearch.trim()} data-testid="location-search-btn">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {searchResults.map((r, i) => (
                    <button key={i} className="w-full text-left px-3 py-2.5 hover:bg-muted/50 text-sm flex items-start gap-2"
                      onClick={() => selectLocation(r)} disabled={savingLocation} data-testid={`location-result-${i}`}>
                      <MapPin className="h-4 w-4 text-[#F05D5E] mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{r.display}</span>
                    </button>
                  ))}
                </div>
              )}

              {currentLocation ? (
                <div className="space-y-1" data-testid="current-location-map">
                  <GoogleMapDraggable
                    lat={currentLocation.lat}
                    lng={currentLocation.lng}
                    height="220px"
                    onPositionChange={async (lat, lng) => {
                      setCurrentLocation(prev => ({ ...prev, lat, lng }));
                      setSavingLocation(true);
                      try {
                        await businessesAPI.updateBusiness({ latitude: lat, longitude: lng });
                        toast.success(t('Ubicacion actualizada', 'Location updated'));
                      } catch { toast.error(t('Error al guardar', 'Save error')); }
                      setSavingLocation(false);
                    }}
                  />
                  <div className="px-3 py-2 bg-muted/30 rounded-b-xl flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#F05D5E] shrink-0" />
                    <span className="text-sm truncate">{currentLocation.address}, {currentLocation.city}, {currentLocation.state}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {t('Arrastra el marcador para ajustar la ubicacion', 'Drag the marker to adjust location')}
                  </p>
                </div>
              ) : (
                <div className="h-[160px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center">
                  <MapPin className="h-8 w-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('Busca tu direccion para ver el mapa', 'Search your address to see the map')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===================== NOTIFICATIONS TAB ===================== */}
        {activeTab === 'notifications' && privateInfo && (
          <Card data-testid="notifications-section">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Bell className="h-5 w-5 text-[#F05D5E]" />
                {t('Notificaciones del negocio', 'Business notifications')}
              </CardTitle>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                {t(
                  'Decide como quieres enterarte cuando un cliente reserve, confirme su pago o cancele una cita. Estas avisos llegan al telefono y correo registrados del negocio.',
                  'Choose how you want to be notified when a client books, confirms payment, or cancels an appointment. Alerts go to the business phone and email on file.'
                )}
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Email toggle */}
              <div className="flex items-start justify-between gap-3 rounded-lg border p-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-lg bg-[#F05D5E]/10 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-[#F05D5E]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{t('Correo electrónico', 'Email')}</p>
                    <p className="text-xs text-muted-foreground truncate">{privateInfo.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(
                        'Recibe el detalle completo de cada nueva reserva y cancelación.',
                        'Get full details of every new booking and cancellation.'
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={privateInfo.notify_email !== false}
                  disabled={savingPrefs}
                  onCheckedChange={(v) => toggleNotifPref('notify_email', v)}
                  data-testid="biz-notify-email-toggle"
                />
              </div>

              {/* SMS toggle */}
              <div className="flex items-start justify-between gap-3 rounded-lg border p-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-lg bg-[#F05D5E]/10 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-5 w-5 text-[#F05D5E]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{t('SMS al celular del negocio', 'SMS to business mobile')}</p>
                    <p className="text-xs text-muted-foreground truncate">{privateInfo.phone || t('Sin numero', 'No number')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(
                        'Avisos rápidos para que no pierdas reservas mientras estás atendiendo.',
                        'Quick alerts so you never miss a booking while serving clients.'
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={privateInfo.notify_sms !== false}
                  disabled={savingPrefs || !privateInfo.phone}
                  onCheckedChange={(v) => toggleNotifPref('notify_sms', v)}
                  data-testid="biz-notify-sms-toggle"
                />
              </div>

              {/* Info note */}
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                <p className="text-xs text-amber-900 leading-relaxed">
                  <strong>{t('Importante:', 'Important:')}</strong>{' '}
                  {t(
                    'Te recomendamos mantener al menos uno activo. Si los apagas todos, no recibirás avisos de nuevas reservas y podrías perder citas. Estas notificaciones nunca contienen publicidad — solo información operativa de tu negocio.',
                    'We recommend keeping at least one enabled. If you turn them all off, you won\'t receive new booking alerts and may miss appointments. These notifications never contain ads — only operational info for your business.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===================== PUSH PREFERENCES TAB (mobile) ===================== */}
        {activeTab === 'notifications' && (
          <NotificationPreferencesCard variant="business" />
        )}

        {/* ===================== BLACKLIST TAB ===================== */}
        {activeTab === 'blacklist' && (
          <Card data-testid="blacklist-section">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <ShieldX className="h-5 w-5 text-[#F05D5E]" />
                {t('Clientes vetados', 'Banned clients')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t('Los clientes vetados no podran ver tu negocio ni realizar reservas.', 'Banned clients won\'t be able to see your business or make bookings.')}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-xl border border-dashed p-4 space-y-3" data-testid="blacklist-add-form">
                <p className="text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" />{t('Agregar cliente al veto', 'Add client to ban list')}</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1"><Mail className="h-3 w-3" /> Email</Label>
                    <Input type="email" placeholder="cliente@email.com" value={blForm.email} onChange={e => setBlForm(p => ({ ...p, email: e.target.value }))} data-testid="blacklist-email" />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1"><Phone className="h-3 w-3" /> {t('Telefono', 'Phone')}</Label>
                    <Input placeholder="+52 555 000 0000" value={blForm.phone} onChange={e => setBlForm(p => ({ ...p, phone: e.target.value }))} data-testid="blacklist-phone" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1">{t('Razon', 'Reason')}</Label>
                    <Input placeholder={t('Opcional...', 'Optional...')} value={blForm.reason} onChange={e => setBlForm(p => ({ ...p, reason: e.target.value }))} data-testid="blacklist-reason" />
                  </div>
                </div>
                <Button className="btn-coral w-full sm:w-auto" onClick={handleAddBlacklist} disabled={adding} data-testid="blacklist-add-btn">
                  {adding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
                  {t('Vetar cliente', 'Ban client')}
                </Button>
              </div>

              <Separator />

              {blacklist.length > 0 ? (
                <div className="space-y-2">
                  {blacklist.map(entry => (
                    <div key={entry.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`blacklist-entry-${entry.id}`}>
                      <Ban className="h-4 w-4 text-red-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1.5">
                          {entry.email && <Badge variant="outline" className="text-xs"><Mail className="h-3 w-3 mr-1" />{entry.email}</Badge>}
                          {entry.phone && <Badge variant="outline" className="text-xs"><Phone className="h-3 w-3 mr-1" />{entry.phone}</Badge>}
                        </div>
                        {entry.reason && <p className="text-xs text-muted-foreground mt-1 truncate">{entry.reason}</p>}
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(entry.created_at).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-50 shrink-0"
                        onClick={() => handleRemoveBlacklist(entry.id)} data-testid={`blacklist-remove-${entry.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed rounded-xl">
                  <Ban className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{t('No tienes clientes vetados', 'No banned clients')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <DeleteAccountCard kind="business" />
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate">{value || '---'}</p>
      </div>
    </div>
  );
}
